import { VStack } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  LibraryElementTypeDto,
  type LibraryElementDto,
  type SelectionCriterionDto,
  useCreateLibraryElementMutation,
  useDeleteLibraryElementMutation,
  useUpdateLibraryElementMutation,
} from "../../autogen/bff-api";
import { useApiError } from "../../common/errors/useApiError";
import { useConfirmationModal } from "../../common/useConfirmationModal";
import { QualificationModal, type QualificationFormValues } from "./QualificationModal";
import { QualificationRequirementList } from "../sourcing-events/buyer/wizard/components/QualificationRequirementList";
import { LibrarySourceSection } from "./LibrarySourceSection";

// t('Your qualification requirements') t('SourceMagnet standard')
// t('Delete requirement') t('Are you sure you want to delete this requirement?') t('Delete')

interface Props {
  elements: LibraryElementDto[];
  orgName: string;
  canManageOrg: boolean;
  addRefusal?: string;
  isSystemAdmin: boolean;
}

type DrawerState =
  | { kind: "add"; global: boolean }
  | { kind: "edit"; element: LibraryElementDto; initialValues: QualificationFormValues };

interface Row {
  element: LibraryElementDto;
  value: SelectionCriterionDto;
}

const rowsOf = (elements: LibraryElementDto[]): Row[] =>
  elements.flatMap((element) => (element.content.selection ? [{ element, value: element.content.selection }] : []));

export const QualificationLibraryTab = ({ elements, orgName, canManageOrg, addRefusal, isSystemAdmin }: Props) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const confirmationModal = useConfirmationModal();
  const [createLibraryElement, { isLoading: isCreating }] = useCreateLibraryElementMutation();
  const [updateLibraryElement, { isLoading: isUpdating }] = useUpdateLibraryElementMutation();
  const [deleteLibraryElement] = useDeleteLibraryElementMutation();
  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const [drawerKey, setDrawerKey] = useState(0);

  const selection = elements.filter((e) => e.content.type === LibraryElementTypeDto.Selection);
  const orgRows = rowsOf(selection.filter((e) => !e.owner.global));
  const globalRows = rowsOf(selection.filter((e) => e.owner.global));

  const openAdd = (global: boolean) => {
    setDrawerKey((k) => k + 1);
    setDrawer({ kind: "add", global });
  };

  const openEdit = (element: LibraryElementDto, value: SelectionCriterionDto) => {
    setDrawerKey((k) => k + 1);
    setDrawer({
      kind: "edit",
      element,
      initialValues: {
        type: value.type,
        name: value.name,
        description: value.description,
        requiresEvidence: value.requiresEvidence,
      },
    });
  };

  const handleSubmit = async (values: SelectionCriterionDto) => {
    try {
      if (drawer?.kind === "add") {
        await createLibraryElement({
          createLibraryElementRequest: {
            content: { type: LibraryElementTypeDto.Selection, selection: values },
            global: drawer.global,
          },
        }).unwrap();
      } else if (drawer?.kind === "edit") {
        await updateLibraryElement({
          updateLibraryElementRequest: {
            id: drawer.element.id,
            content: { type: LibraryElementTypeDto.Selection, selection: values },
            tags: drawer.element.tags,
          },
        }).unwrap();
      }
      setDrawer(null);
    } catch (e) {
      trigger(e);
    }
  };

  const handleDelete = (element: LibraryElementDto) => {
    confirmationModal.show({
      title: t("Delete requirement"),
      description: t("Are you sure you want to delete this requirement?"),
      confirmButtonText: t("Delete") ?? "",
      confirmButtonColor: "red",
      onConfirmed: async () => {
        try {
          await deleteLibraryElement({ id: element.id }).unwrap();
        } catch (e) {
          trigger(e);
        }
      },
    });
  };

  return (
    <VStack align="stretch" spacing="8">
      <LibrarySourceSection
        title={t("Your qualification requirements")}
        scope="org"
        orgName={orgName}
        editable={canManageOrg}
        addRefusal={addRefusal}
        addLabel={t("Add requirement")}
        onAdd={() => openAdd(false)}
        isEmpty={orgRows.length === 0}
        emptyText={t("No qualification requirements of your own yet.")}
      >
        <QualificationRequirementList
          layout="framed"
          items={orgRows.map((r) => r.value)}
          readOnly={!canManageOrg}
          onAdd={() => openAdd(false)}
          onEdit={(i) => openEdit(orgRows[i].element, orgRows[i].value)}
          onDelete={(i) => handleDelete(orgRows[i].element)}
        />
      </LibrarySourceSection>

      <LibrarySourceSection
        title={t("SourceMagnet standard")}
        scope="global"
        editable={isSystemAdmin}
        addRefusal={addRefusal}
        addLabel={t("Add requirement")}
        onAdd={() => openAdd(true)}
        isEmpty={globalRows.length === 0}
        emptyText={t("No SourceMagnet standard qualification requirements yet.")}
      >
        <QualificationRequirementList
          layout="framed"
          items={globalRows.map((r) => r.value)}
          readOnly={!isSystemAdmin}
          onAdd={() => openAdd(true)}
          onEdit={(i) => openEdit(globalRows[i].element, globalRows[i].value)}
          onDelete={(i) => handleDelete(globalRows[i].element)}
        />
      </LibrarySourceSection>

      <QualificationModal
        key={drawerKey}
        isOpen={drawer !== null}
        isSaving={isCreating || isUpdating}
        mode={drawer?.kind ?? "add"}
        defaultValues={drawer?.kind === "edit" ? drawer.initialValues : undefined}
        onSubmit={handleSubmit}
        onClose={() => setDrawer(null)}
      />
      {confirmationModal.modal}
    </VStack>
  );
};
